// Firebase lazy loader for v9 modular SDK
// Dynamically imports Firebase modules only when needed

let firebaseCache = null;

export const getFirebase = async () => {
  if (firebaseCache) {
    return firebaseCache;
  }

  const [
    { initializeApp },
    {
      getAuth,
      signInWithEmailAndPassword,
      createUserWithEmailAndPassword,
      signOut,
      sendPasswordResetEmail,
      confirmPasswordReset,
      sendEmailVerification,
      applyActionCode,
      updateProfile,
    },
    { getFirestore, doc, getDoc, getDocs, collection, setDoc, addDoc, serverTimestamp },
  ] = await Promise.all([
    import(/* webpackChunkName: 'chunk-firebase-app' */ 'firebase/app'),
    import(/* webpackChunkName: 'chunk-firebase-auth' */ 'firebase/auth'),
    import(/* webpackChunkName: 'chunk-firebase-firestore' */ 'firebase/firestore'),
  ]);

  const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  };

  // Validate configuration - only throw if Firebase was explicitly requested
  if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
    throw new Error(
      'Firebase auth requested but not configured. Set VITE_FIREBASE_* environment variables or use JWT auth (default).'
    );
  }

  const app = initializeApp(firebaseConfig);
  const auth = getAuth(app);
  const db = getFirestore(app);

  firebaseCache = {
    app,
    auth,
    db,
    // Firebase Auth methods
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut,
    sendPasswordResetEmail,
    confirmPasswordReset,
    sendEmailVerification,
    applyActionCode,
    updateProfile,
    // Firestore methods
    doc,
    getDoc,
    getDocs,
    collection,
    setDoc,
    addDoc,
    serverTimestamp,
  };

  return firebaseCache;
};

// Reset cache for testing
export const resetFirebaseCache = () => {
  firebaseCache = null;
};
